// src/Components/ExportButtons.js
import React from 'react';
import { Button, CircularProgress, Stack } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import buildQrFilename from '../utils/buildQrFilename';

const loadCanvasUtils = () => import('../CanvasUtils');

const ExportButtons = ({
  targetUrl,
  shortUrl,
  canvasRef,
  onSave,
  linkType,
  disableSave = false,
  isSaved = false,
  foregroundColor,
  backgroundColor,
  logoFileOrUrl,
  logoScale,
  dotStyle,
  cornerSquareStyle,
  cornerDotStyle,
  preset,
  shortCode,
  utmCampaign,
  utmSource,
  isSaving = false,
}) => {
  const isLinkOnly = linkType === 'link';
  const exportLocked = !isSaved || isSaving;
  const primaryLabel = isSaved
    ? (isLinkOnly ? 'Link Saved' : 'QR Saved')
    : (isLinkOnly ? 'Generate Link' : 'Generate QR');
  const filenamePng = buildQrFilename({
    shortCode,
    utmCampaign,
    utmSource,
    extension: 'png',
  });
  const filenameTransparentPng = filenamePng.replace(/\.png$/i, '-transparent.png');
  const filenameSvg = buildQrFilename({
    shortCode,
    utmCampaign,
    utmSource,
    extension: 'svg',
  });

  const handleSvgExport = async () => {
    if (!targetUrl) return;
    try {
      const { exportQrSvg } = await loadCanvasUtils();
      await exportQrSvg({
        text: targetUrl,
        foregroundColor,
        backgroundColor,
        logoFileOrUrl: canvasRef?.current?.hasLogo ? logoFileOrUrl : null,
        logoScale,
        dotStyle,
        cornerSquareStyle,
        cornerDotStyle,
        preset,
        filename: filenameSvg,
      });
    } catch (err) {
      console.error('Failed to export SVG', err);
    }
  };

  const handleCopyQr = async () => {
    const { copyCanvasImageToClipboard } = await loadCanvasUtils();
    await copyCanvasImageToClipboard(canvasRef);
  };

  const handlePngExport = async () => {
    const { exportPng } = await loadCanvasUtils();
    await exportPng(canvasRef, filenamePng);
  };

  const handleTransparentPngExport = async () => {
    if (!targetUrl) return;
    const { exportQrPng } = await loadCanvasUtils();
    await exportQrPng({
      text: targetUrl,
      foregroundColor,
      backgroundColor,
      transparentBackground: true,
      logoFileOrUrl: canvasRef?.current?.hasLogo ? logoFileOrUrl : null,
      logoScale,
      dotStyle,
      cornerSquareStyle,
      cornerDotStyle,
      preset,
      filename: filenameTransparentPng,
    });
  };

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      justifyContent="flex-start"
      mt={0.5}
      mb={0}
      flexWrap="wrap"
      useFlexGap
    >
      <Button
        variant="outlined"
        onClick={() => navigator.clipboard.writeText(shortUrl)}
        startIcon={<ContentCopyIcon />}
        disabled={!shortUrl || isSaving}
      >
        Copy Short Link
      </Button>
      {!isLinkOnly && (
        <>
          <Button
            variant="outlined"
            onClick={handleCopyQr}
            startIcon={<ImageIcon />}
            disabled={exportLocked}
          >
            Copy QR
          </Button>
          <Button
            variant="outlined"
            onClick={handlePngExport}
            startIcon={<DownloadIcon />}
            disabled={exportLocked}
          >
            Download QR PNG
          </Button>
          <Button
            variant="outlined"
            onClick={handleTransparentPngExport}
            startIcon={<DownloadIcon />}
            disabled={exportLocked}
          >
            Download Transparent PNG
          </Button>
          <Button
            variant="outlined"
            onClick={handleSvgExport}
            startIcon={<DownloadIcon />}
            disabled={exportLocked}
          >
            Download QR SVG
          </Button>
        </>
      )}
      <Button
        variant="contained"
        onClick={onSave}
        startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        disabled={disableSave || isSaving || isSaved}
      >
        {isSaving ? 'Generating...' : primaryLabel}
      </Button>
    </Stack>
  );
};

export default ExportButtons;
